import { supabase } from '../lib/supabase';

/**
 * Subscribe to leaderboard changes for a specific tournament
 * @param {string} tournamentId - Tournament ID to subscribe to
 * @param {function} onRankChange - Callback when rank changes occur
 * @param {function} onPointsUpdate - Callback when points are updated
 * @returns {object} Subscription channel
 */
export const subscribeToLeaderboardChanges = (tournamentId, onRankChange, onPointsUpdate) => {
  const channel = supabase
    ?.channel(`leaderboard_${tournamentId}`)
    ?.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'leaderboard',
        filter: `tournament_id=eq.${tournamentId}`
      },
      (payload) => {
        const oldData = payload?.old;
        const newData = payload?.new;

        // Check for rank changes
        if (oldData?.rank !== newData?.rank) {
          onRankChange?.({
            userId: newData?.user_id,
            tournamentId: newData?.tournament_id,
            oldRank: oldData?.rank,
            newRank: newData?.rank,
            change: oldData?.rank - newData?.rank,
            timestamp: new Date()?.toISOString()
          });
        }

        // Check for points updates
        if (oldData?.total_points !== newData?.total_points) {
          onPointsUpdate?.({
            userId: newData?.user_id,
            tournamentId: newData?.tournament_id,
            oldPoints: oldData?.total_points,
            newPoints: newData?.total_points,
            pointsGained: newData?.total_points - oldData?.total_points,
            rank: newData?.rank,
            timestamp: new Date()?.toISOString()
          });
        }
      }
    )
    ?.subscribe();

  return channel;
};

/**
 * Subscribe to match status changes
 * @param {string} tournamentId - Tournament ID to subscribe to
 * @param {function} onMatchUpdate - Callback when match status changes
 * @returns {object} Subscription channel
 */
export const subscribeToMatchUpdates = (tournamentId, onMatchUpdate) => {
  const channel = supabase
    ?.channel(`matches_${tournamentId}`)
    ?.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'matches',
        filter: `tournament_id=eq.${tournamentId}`
      },
      (payload) => {
        const oldData = payload?.old;
        const newData = payload?.new;

        // Check for status changes (upcoming -> live -> completed)
        if (oldData?.status !== newData?.status) {
          onMatchUpdate?.({
            matchId: newData?.id,
            tournamentId: newData?.tournament_id,
            oldStatus: oldData?.status,
            newStatus: newData?.status,
            team1Id: newData?.team1_id,
            team2Id: newData?.team2_id,
            winnerId: newData?.winner_id,
            timestamp: new Date()?.toISOString()
          });
        }

        // Check for result updates
        if (newData?.status === 'completed' && newData?.winner_id && !oldData?.winner_id) {
          onMatchUpdate?.({
            matchId: newData?.id,
            tournamentId: newData?.tournament_id,
            type: 'result',
            winnerId: newData?.winner_id,
            team1Score: newData?.team1_score,
            team2Score: newData?.team2_score,
            timestamp: new Date()?.toISOString()
          });
        }
      }
    )
    ?.subscribe();

  return channel;
};

/**
 * Subscribe to tournament status changes
 * @param {function} onTournamentUpdate - Callback when tournament status changes
 * @returns {object} Subscription channel
 */
export const subscribeToTournamentUpdates = (onTournamentUpdate) => {
  const channel = supabase
    ?.channel('tournaments_updates')
    ?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'tournaments'
      },
      (payload) => {
        const eventType = payload?.eventType;
        const data = payload?.new || payload?.old;

        if (eventType === 'INSERT') {
          onTournamentUpdate?.({
            type: 'new_tournament',
            tournamentId: data?.id,
            tournamentName: data?.name,
            startDate: data?.start_date,
            status: data?.status,
            timestamp: new Date()?.toISOString()
          });
        } else if (eventType === 'UPDATE') {
          const oldData = payload?.old;
          const newData = payload?.new;

          if (oldData?.status !== newData?.status) {
            onTournamentUpdate?.({
              type: 'status_change',
              tournamentId: newData?.id,
              tournamentName: newData?.name,
              oldStatus: oldData?.status,
              newStatus: newData?.status,
              timestamp: new Date()?.toISOString()
            });
          }
        }
      }
    )
    ?.subscribe();

  return channel;
};

/**
 * Subscribe to user-specific leaderboard changes across all tournaments
 * @param {string} userId - User ID to track
 * @param {function} onUserRankChange - Callback when user's rank changes
 * @returns {object} Subscription channel
 */
export const subscribeToUserLeaderboardChanges = (userId, onUserRankChange) => {
  const channel = supabase
    ?.channel(`user_leaderboard_${userId}`)
    ?.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'leaderboard',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        const oldData = payload?.old;
        const newData = payload?.new;

        // Notify on any changes to user's leaderboard entry
        if (oldData?.rank !== newData?.rank || oldData?.total_points !== newData?.total_points) {
          onUserRankChange?.({
            userId: newData?.user_id,
            tournamentId: newData?.tournament_id,
            oldRank: oldData?.rank,
            newRank: newData?.rank,
            oldPoints: oldData?.total_points,
            newPoints: newData?.total_points,
            pointsGained: newData?.total_points - oldData?.total_points,
            rankChange: oldData?.rank - newData?.rank,
            accuracy: newData?.accuracy,
            timestamp: new Date()?.toISOString()
          });
        }
      }
    )
    ?.subscribe();

  return channel;
};

/**
 * Unsubscribe from a channel
 * @param {object} channel - Channel to unsubscribe from
 */
export const unsubscribeChannel = async (channel) => {
  if (channel) {
    await supabase?.removeChannel(channel);
  }
};

/**
 * Unsubscribe from multiple channels
 * @param {array} channels - Array of channels to unsubscribe from
 */
export const unsubscribeChannels = async (channels) => {
  if (channels && channels?.length > 0) {
    await Promise.all(channels?.map(channel => supabase?.removeChannel(channel)));
  }
};
